/**
 * Parse / format AgentTxEvent.detail for Liquidate & FillAuction rows.
 * Gateway writes:
 * paidE8=…;commissionE8=…;assets=ICP:e8,…;bonusBps=…;profitE8=…
 */

export type ParsedLiqTxDetail = {
	paidE8: number | null;
	commissionE8: number | null;
	assets: Array<{ token: string; amountE8: number }>;
	bonusBps: number | null;
	/** Estimated liquidator edge at commit (USDM e8). Absent on legacy rows. */
	profitE8: number | null;
};

export type SeizedAggregateRow = {
	token: string;
	amountE8: number;
	txCount: number;
};

export type LiqTxPopupFields = {
	paidUsdm: string | null;
	commissionUsdm: string | null;
	totalSpentUsdm: string | null;
	profitUsdm: string | null;
	assetsLabel: string;
	assets: Array<{ token: string; amountLabel: string }>;
	bonusBps: number | null;
	legacy: boolean;
};

function tokenDecimals(token: string): number {
	if (token === "ckBTC") return 8;
	if (token === "ckETH") return 8;
	return 8;
}

export function formatTokenE8(amountE8: number, token: string): string {
	const dec = tokenDecimals(token);
	const v = amountE8 / 10 ** dec;
	if (token === "ckBTC" || token === "ckETH") {
		return v.toFixed(6).replace(/\.?0+$/, "");
	}
	return v.toFixed(4).replace(/\.?0+$/, "");
}

function e8ToUsdmLabel(e8: number | null): string | null {
	if (e8 == null || !Number.isFinite(e8)) return null;
	return (e8 / 1e8).toFixed(2);
}

export function parseLiqTxDetail(detail: string): ParsedLiqTxDetail | null {
	if (!detail || !detail.includes("paidE8=")) return null;
	const parts = detail.split(";");
	const map = new Map<string, string>();
	for (const p of parts) {
		const i = p.indexOf("=");
		if (i <= 0) continue;
		map.set(p.slice(0, i), p.slice(i + 1));
	}
	const paidRaw = map.get("paidE8");
	const commissionRaw = map.get("commissionE8");
	const assetsRaw = map.get("assets") ?? "";
	const bonusRaw = map.get("bonusBps");
	const profitRaw = map.get("profitE8");
	const paidE8 =
		paidRaw != null && Number.isFinite(Number(paidRaw)) ? Number(paidRaw) : null;
	const commissionE8 =
		commissionRaw != null && Number.isFinite(Number(commissionRaw))
			? Number(commissionRaw)
			: null;
	const bonusBps =
		bonusRaw != null && Number.isFinite(Number(bonusRaw))
			? Number(bonusRaw)
			: null;
	const profitE8 =
		profitRaw != null && Number.isFinite(Number(profitRaw))
			? Number(profitRaw)
			: null;
	const assets: ParsedLiqTxDetail["assets"] = [];
	if (assetsRaw.length > 0) {
		for (const chunk of assetsRaw.split(",")) {
			const [token, amt] = chunk.split(":");
			if (!token || amt == null) continue;
			const amountE8 = Number(amt);
			if (!Number.isFinite(amountE8) || amountE8 <= 0) continue;
			assets.push({ token, amountE8 });
		}
	}
	return { paidE8, commissionE8, assets, bonusBps, profitE8 };
}

/** Human subtitle for Bot transaction rows. */
export function formatLiqTxActivitySub(
	detail: string,
	fallbackUsdmSpentE8: number,
): string {
	const parsed = parseLiqTxDetail(detail);
	if (!parsed || parsed.paidE8 == null) {
		const usdm =
			fallbackUsdmSpentE8 > 0
				? `${(fallbackUsdmSpentE8 / 1e8).toFixed(2)} USDM`
				: "";
		return detail || usdm || "economic action";
	}
	const paid = (parsed.paidE8 / 1e8).toFixed(2);
	const commission = ((parsed.commissionE8 ?? 0) / 1e8).toFixed(2);
	const got =
		parsed.assets.length > 0
			? parsed.assets
					.map((a) => `${formatTokenE8(a.amountE8, a.token)} ${a.token}`)
					.join(" + ")
			: "no collateral";
	const profit =
		parsed.profitE8 != null
			? ` · profit ~${(parsed.profitE8 / 1e8).toFixed(2)} USDM`
			: "";
	return `paid ${paid} USDM · commission ${commission} USDM · got ${got}${profit}`;
}

export function formatLiqTxPopupFields(
	detail: string,
	fallbackUsdmSpentE8: number,
): LiqTxPopupFields {
	const parsed = parseLiqTxDetail(detail);
	if (!parsed || parsed.paidE8 == null) {
		const spent =
			fallbackUsdmSpentE8 > 0
				? (fallbackUsdmSpentE8 / 1e8).toFixed(2)
				: null;
		return {
			paidUsdm: spent,
			commissionUsdm: null,
			totalSpentUsdm: spent,
			profitUsdm: null,
			assetsLabel: detail || "—",
			assets: [],
			bonusBps: null,
			legacy: true,
		};
	}
	const paid = parsed.paidE8;
	const commission = parsed.commissionE8 ?? 0;
	return {
		paidUsdm: e8ToUsdmLabel(paid),
		commissionUsdm: e8ToUsdmLabel(commission),
		totalSpentUsdm: e8ToUsdmLabel(paid + commission),
		profitUsdm: e8ToUsdmLabel(parsed.profitE8),
		assetsLabel:
			parsed.assets.length > 0
				? parsed.assets
						.map((a) => `${formatTokenE8(a.amountE8, a.token)} ${a.token}`)
						.join(" + ")
				: "none",
		assets: parsed.assets.map((a) => ({
			token: a.token,
			amountLabel: formatTokenE8(a.amountE8, a.token),
		})),
		bonusBps: parsed.bonusBps,
		legacy: parsed.profitE8 == null,
	};
}

export function aggregateSeizedFromTxHistory(
	events: Array<{ kind: string; detail: string }>,
): SeizedAggregateRow[] {
	const map = new Map<string, SeizedAggregateRow>();
	for (const e of events) {
		if (e.kind !== "Liquidate" && e.kind !== "FillAuction") continue;
		const parsed = parseLiqTxDetail(e.detail);
		if (!parsed) continue;
		const seen = new Set<string>();
		for (const a of parsed.assets) {
			const cur = map.get(a.token) ?? {
				token: a.token,
				amountE8: 0,
				txCount: 0,
			};
			cur.amountE8 += a.amountE8;
			if (!seen.has(a.token)) {
				cur.txCount += 1;
				seen.add(a.token);
			}
			map.set(a.token, cur);
		}
	}
	return Array.from(map.values()).sort((a, b) =>
		a.token.localeCompare(b.token),
	);
}
